import React, { useState, useEffect } from 'react';
import { useWizard } from '../../context/WizardContext';
import NavigationButtons from '../shared/NavigationButtons';
import { FileEdit, Copy, Check, AlertTriangle, Sparkles, Download } from 'lucide-react';

const C8_FrameworkSection = () => {
  const { dataC, dataA, updateDataRaw, previousStep, completeWizard, switchWizard, wizardCComplete } = useWizard();
  const [generatedText, setGeneratedText] = useState('');
  const [copied, setCopied] = useState(false);
  const [isEditing, setIsEditing] = useState(false);

  // Auto-complete Wizard C when reaching this final step
  useEffect(() => {
    if (!wizardCComplete) {
      completeWizard('C');
    }
  }, [wizardCComplete, completeWizard]);

  const generateFrameworkSection = () => {
    const constructs = dataC?.constructs || [];
    const levels = dataC?.levels || [];
    const connections = dataC?.diagramConnections || [];
    const nodes = dataC?.diagramNodes || [];
    const assumptions = dataC?.assumptions || {};
    const propositions = dataC?.propositions || [];
    const paradigm = dataA?.philosophy || 'the selected paradigm';

    let text = `## Conceptual Framework\n\n`;

    text += `### Overview\n`;
    
    if (constructs.length === 0) {
      text += `This study develops a conceptual framework grounded in ${paradigm}. `;
      text += `[Please add constructs in Step 1 to complete this section.]\n\n`;
    } else {
      text += `This study develops a conceptual framework grounded in ${paradigm} to examine `;
      text += `the relationships between ${constructs.map(c => c.name).filter(Boolean).join(', ') || '[constructs]'}. `;
      
      if (levels.length > 0) {
        text += `The framework operates at the ${levels.map(l => l.charAt(0).toUpperCase() + l.slice(1)).join(', ')} level${levels.length > 1 ? 's' : ''} of analysis.\n\n`;
      } else {
        text += `[Please select levels of analysis in Step 2.]\n\n`;
      }
    }

    text += `### Key Constructs\n`;
    constructs.forEach((c, i) => {
      text += `**${c.name}** (${c.type}): ${c.definition || 'Definition to be added.'}\n\n`;
    });

    if (connections.length > 0) {
      text += `### Theoretical Relationships\n`;
      text += `The framework posits the following relationships:\n\n`;
      
      connections.forEach((conn, i) => {
        const fromNode = nodes.find(n => n.id === conn.from);
        const toNode = nodes.find(n => n.id === conn.to);
        if (fromNode && toNode) {
          const relationType = conn.type === 'direct' ? 'positive' : 
                              conn.type === 'inverse' ? 'negative' : 'moderated';
          text += `- ${fromNode.name} has a ${relationType} relationship with ${toNode.name}`;
          if (conn.label) text += ` (${conn.label})`;
          text += `.\n`;
        }
      });
      text += `\n`;
    }

    const allAssumptions = Object.values(assumptions).flat();
    if (allAssumptions.length > 0) {
      text += `### Underlying Assumptions\n`;
      text += `This framework is built upon the following assumptions:\n\n`;
      allAssumptions.forEach((a, i) => {
        text += `${i + 1}. ${a}\n`;
      });
      text += `\n`;
    }

    if (propositions.length > 0) {
      text += `### Research Propositions\n`;
      text += `Based on the theoretical relationships, the following propositions are advanced:\n\n`;
      propositions.forEach((p, i) => {
        const propText = p.text.replace(/^P\d+:\s*/, '');
        text += `**P${i + 1}:** ${propText}\n\n`;
      });
    }

    text += `### Framework Justification\n`;
    text += `This conceptual framework integrates insights from [cite relevant theories] to address `;
    text += `[research gap]. The framework extends existing knowledge by [contribution]. `;
    text += `Its application is bounded by [limitations/scope].\n\n`;

    text += `---\n`;
    text += `*Note: This text is auto-generated as a starting point. Please revise and paraphrase `;
    text += `to reflect your unique research context and avoid similarity with other users.*`;

    return text;
  };

  useEffect(() => {
    if (!generatedText) {
      setGeneratedText(generateFrameworkSection());
    }
  }, [dataC]);

  const regenerate = () => {
    setGeneratedText(generateFrameworkSection());
    setIsEditing(false);
  };

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(generatedText);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Copy failed:', err);
    }
  };

  const downloadAsText = () => {
    const blob = new Blob([generatedText], { type: 'text/markdown' });
    const link = document.createElement('a');
    link.download = 'framework-section.md';
    link.href = URL.createObjectURL(blob);
    link.click();
    URL.revokeObjectURL(link.href);
  };

  const handleComplete = () => {
    updateDataRaw({ 'wizardC.frameworkText': generatedText });
    completeWizard('C');
    switchWizard('A');
  };

  return (
    <div className="wizard-step">
      <div className="step-header">
        <div className="step-icon writing-icon">
          <FileEdit size={32} />
        </div>
        <div>
          <h2>Step 10: Framework Section</h2>
          <p className="step-description">Generate and customize your framework write-up</p>
        </div>
      </div>

      <div className="step-content">
        <div className="plagiarism-warning">
          <AlertTriangle size={20} />
          <div>
            <strong>Important:</strong> This text is auto-generated as a starting point. 
            You must edit and paraphrase this content to make it unique to your research 
            and avoid similarity with frameworks created by other users of this app.
          </div>
        </div>

        <div className="text-actions">
          <button className="action-btn regenerate" onClick={regenerate}>
            <Sparkles size={18} />
            Regenerate
          </button>
          <button className="action-btn copy" onClick={copyToClipboard}>
            {copied ? <Check size={18} /> : <Copy size={18} />}
            {copied ? 'Copied!' : 'Copy to Clipboard'}
          </button>
          <button className="action-btn download" onClick={downloadAsText}>
            <Download size={18} />
            Download as Markdown
          </button>
        </div>

        <div className="generated-text-container">
          {isEditing ? (
            <textarea
              className="text-editor"
              value={generatedText}
              onChange={(e) => setGeneratedText(e.target.value)}
              rows={25}
            />
          ) : (
            <div 
              className="text-preview"
              onClick={() => setIsEditing(true)}
            >
              {generatedText.split('\n').map((line, i) => {
                if (line.startsWith('## ')) {
                  return <h2 key={i}>{line.replace('## ', '')}</h2>;
                } else if (line.startsWith('### ')) {
                  return <h3 key={i}>{line.replace('### ', '')}</h3>;
                } else if (line.startsWith('**') && line.includes(':**')) {
                  const [label, content] = line.split(':**');
                  return <p key={i}><strong>{label.replace('**', '')}:</strong>{content?.replace('**', '')}</p>;
                } else if (line.startsWith('- ')) {
                  return <li key={i}>{line.replace('- ', '')}</li>;
                } else if (line.startsWith('*Note:')) {
                  return <p key={i} className="note">{line.replace(/\*/g, '')}</p>;
                } else if (line.match(/^\d+\./)) {
                  return <p key={i} className="numbered">{line}</p>;
                } else if (line === '---') {
                  return <hr key={i} />;
                } else if (line.trim()) {
                  return <p key={i}>{line}</p>;
                }
                return null;
              })}
              <p className="click-to-edit">(Click anywhere to edit)</p>
            </div>
          )}
        </div>

        {isEditing && (
          <button className="preview-btn" onClick={() => setIsEditing(false)}>
            Exit Edit Mode
          </button>
        )}

        <div className="completion-box">
          <h4>Framework Builder Complete!</h4>
          <p>
            You've successfully built your conceptual framework. You can now use the 
            exported diagram and generated text in your research proposal or thesis.
          </p>
        </div>
      </div>

      <NavigationButtons
        onBack={previousStep}
        onContinue={handleComplete}
        canContinue={true}
        continueLabel="Complete Wizard C"
      />
    </div>
  );
};

export default C8_FrameworkSection;
